import { TransformFn } from "objectro/lib/types";
import { convertDateToString } from "smoldate";
import { POJO, WithIdentifier, Customer } from "../../types";
import { CustomerFormStateObject } from "../../hooks/useCustomerFormState";
import {
  get,
  isString,
  toArray,
  stripNullProperties,
  getISO8601DateStr
} from "../../utils/helpers";
import debug from "../../utils/debug";

// This is what the DDC API wants, which is slightly different to what
// we store in the customerData to support the form.
interface DDCCustomerAddress {
  streetAddress: string | (string | any)[] | null | undefined;
  addressLocality: string | null | undefined;
  addressRegion: string | (POJO & WithIdentifier) | null | undefined;
  postalCode: string | number | null | undefined;
  addressCountry: string | (POJO & WithIdentifier) | null | undefined;
}

/**
 * This will treat the given value as an object to transform:
 *
 * ```typescript
 *   transform({ streetAddress: "" }, transformPostalAddressToInternalAddress)
 *
 *   // Outputs:
 *   // {
 *   //   streetAddress1: null,
 *   //   streetAddress2: null,
 *   //   streetAddress3: null,
 *   //   addressLocality: null,
 *   //   addressRegion: null,
 *   //   postalCode: null,
 *   //   addressCountry: null
 *   // }
 * ```
 *
 * This means you can use it to target a specific property in the DDCCustomer:
 *
 * ```typescript
 *   const transformMap: TransformMap = {
 *     address: transformPostalAddressToInternalAddress
 *   }
 *
 *   transform({ address: { streetAddress: "" } }, transformMap)
 *
 *   // Outputs:
 *   // {
 *   //   streetAddress1: null,
 *   //   streetAddress2: null,
 *   //   streetAddress3: null,
 *   //   addressLocality: null,
 *   //   addressRegion: null,
 *   //   postalCode: null,
 *   //   addressCountry: null
 *   // }
 * ```
 */
export const transformPostalAddressToInternalAddress: TransformFn = (
  value: Partial<DDCCustomerAddress>
) => {
  const _streetAddress = get(value, "fullStreetAddress", null);
  const streetAddress: string | any[] = _streetAddress
    ? isString(_streetAddress)
      ? toArray(String(_streetAddress), /,\s*/)
      : _streetAddress
    : [null, null, null];

  return {
    streetAddress1: streetAddress[0],
    streetAddress2: streetAddress[1],
    streetAddress3: streetAddress[2],
    addressLocality: get(value, "city", null),
    addressRegion: get(value, "region", null),
    postalCode: get(value, "postalCode", null),
    addressCountry: get(value, "country")
  };
};

/**
 * The internal address is always on the input object.
 *
 * We pluck the values from the input object, as targetting a single
 * property (for the moment) won't be useful (yet).
 */
export const transformInternalAddressToPostalAddress: TransformFn = (
  value: Partial<CustomerFormStateObject>,
  propName?: string,
  input?: any
) => {
  const _postalAddress: POJO | null = stripNullProperties({
    fullStreetAddress:
      [
        get(input, "streetAddress1", null),
        get(input, "streetAddress2", null),
        get(input, "streetAddress3", null)
      ]
        .filter(x => x)
        .join(",") || null,
    streetAddress1: get(input, "streetAddress1", null),
    streetAddress2: get(input, "streetAddress2", null),
    city: get(input, "addressLocality", null),
    region: get(input, "addressRegion", null),
    postalCode: get(input, "postalCode", null),
    country: get(input, "addressCountry", null)
  });

  return _postalAddress;
};

export const valueToIdentifier = (
  value: string | number | null | undefined
) => {
  if (value) {
    return { identifier: value };
  }
  return null;
};

export const valueToTypedIdentifierFn = (type: string): TransformFn => (
  value: string | number | null | undefined
): any => {
  if (value) {
    return { "@type": type, identifier: value };
  }
  return null;
};

export const valueFromIdentifier: TransformFn = (value: any): any => {
  if (value && value.identifier) {
    return value.identifier;
  }
  return null;
};

export const concat = (
  input: any,
  firstNameField: string,
  lastNameField: string
) => {
  const firstName = get(input, firstNameField);
  const lastName = get(input, lastNameField);
  if (!firstName || !lastName) {
    return firstName || lastName;
  }
  const name = firstName + " " + lastName;
  return name.trim();
};

// When returning an object, it'll be merged back into the destination object
// Using this allows us to merge it in at a specific point
export const mergeTo = (
  asPropName: string,
  pipeFn: TransformFn
): TransformFn => (
  value: any,
  propName?: any,
  input?: any,
  output?: any
): any => {
  const transformedValue = pipeFn(value, propName, input, output);
  if (transformedValue !== undefined) {
    return {
      [asPropName]: transformedValue
    };
  }
};

// Map the DDC Backend Customer object to the customerDataState
// DDCCustomer -> customerFormState
export const fromDDCCustomer = {
  identifier: "identifier",
  gender: "gender",
  title: "honorificPrefix",
  firstName: "givenName",
  alternateFirstName1: "alternateGivenName1",
  alternateFirstName2: "alternateGivenName2",
  lastName: "familyName",
  alternateLastName1: "alternateFamilyName1",
  alternateLastName2: "alternateFamilyName2",
  // In case we get a full date-time string, we convert to just a YYYY-MM-DD string
  birthDate: (value: string | null | undefined) => {
    return {
      birthDate: value ? convertDateToString(value) : null
    };
  },
  nationality: "nationality",
  email: "email",
  homePhoneNumber: "telephone",
  mobilePhoneNumber: "mobileTelephone",
  wechatId: "wechatId",
  lineId: "lineId",
  // This won't be mapped to a nested object within the customer data object
  // The internal data model requires a shallow object
  address: transformPostalAddressToInternalAddress,
  appsConsent: "appsConsent",
  emailConsent: "emailConsent",
  postConsent: "postConsent",
  phoneConsent: "phoneConsent",
  advisorCode: "salesPerson"
};

// Map the internal data model to the DDC Backend Customer object
// customerFormState -> DDCCustomer
export const toDDCCustomer = {
  gender: "gender",
  identifier: "identifier",
  honorificPrefix: "title",
  givenName: "firstName",
  familyName: "lastName",
  alternateGivenName1: "alternateFirstName1",
  alternateGivenName2: "alternateFirstName2",
  alternateFamilyName1: "alternateLastName1",
  alternateFamilyName2: "alternateLastName2",
  alternateFullName1: (value: any, propName?: string, input?: any) => {
    return {
      alternateFullName1: concat(
        input,
        "alternateGivenName1",
        "alternateFamilyName1"
      )
    };
  },
  alternateFullName2: (value: any, propName?: string, input?: any) => {
    return {
      alternateFullName2: concat(
        input,
        "alternateGivenName2",
        "alternateFamilyName2"
      )
    };
  },
  // In case the date is not a string, we convert to a YYYY-MM-DD string
  birthDate: (value: Date | number | string | null | undefined) => {
    return {
      birthDate: value ? new Date(value).toISOString() : null
    };
  },
  birthDateYear: (value: number | string | null | undefined) => {
    return {
      birthDateYear: value === "0000" ? null : value
    };
  },
  birthDateMonth: "birthDateMonth",
  birthDateDay: "birthDateDay",
  nationality: "nationality",
  eReceipt: "eReceipt",
  email: "email",
  telephone: "homePhoneNumber",
  mobileTelephone: "mobilePhoneNumber",
  wechatId: "wechatId",
  lineId: "lineId",
  // This will map the shallow InternalAddress properties to a
  // nested object within the DDCCustomer object.
  address: mergeTo("address", transformInternalAddressToPostalAddress),
  appsConsent: "appsConsent",
  emailConsent: "emailConsent",
  postConsent: "postConsent",
  phoneConsent: "phoneConsent",
  salesPerson: "advisorCode",
  creationStore: "storeCode",
  storeCountry: "storeCountry",
  storeName: "storeName",
  storeLocation: "storeLocation",
  preferredLanguage: "preferredLanguage",
  signatureData: "signatureData",
  notes: "notes",
  // customFields: may be defined in config and handled in api.ts
  creationDate: () => {
    return {
      creationDate: getISO8601DateStr()
    };
  }
};

export const customerRequestToXml = function(customerData: Customer) {
  let cylandObj = toCylandObj(customerData);

  let xmlStr = toXML(cylandObj);

  // fix datas nodes (must be unique in json, so we rename it here)
  // <datas_postal>, <data_email>, <data_phone>, <datas_global> => <datas>
  let datasNodesRegex = /datas_\w+>/gm;
  xmlStr = xmlStr.replace(datasNodesRegex, "datas>");

  let xmlPrefix = `<?xml version="1.0" encoding="UTF-8"?>
  <customerListType xmlns="http://com/cylande/unitedretail/customer/service/common/CustomerEngineService/" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">`;
  xmlStr = xmlPrefix + xmlStr + "</customerListType>";
  return xmlStr;
};

export const toCylandObj = function(customerData: Customer) {
  debug("[api] toCylandObj:", customerData);
  let birthdayParts: string[] | null = null;
  if (customerData.birthDate) {
    birthdayParts = customerData.birthDate.split("T");
    birthdayParts = birthdayParts[0].split("-");
  }
  let now: Date = new Date();
  now.setMilliseconds(0);
  // add two hours for +02:00 GMT
  now.setTime(now.getTime() + 120 * 60 * 1000);
  // place +02:00 time zone in string
  let nowStr: string = now.toISOString().replace(".000Z", "+02:00");
  let address = customerData.address;

  let cylandObj = {
    values: {
      externalCodes: {
        typeCode: "D",
        code: get(customerData, "signatureId", null)
      },
      person: {
        gender: get(customerData, "title", null),
        firstName: customerData.firstName,
        lastName: customerData.lastName,
        birthInfos: {
          day: birthdayParts ? birthdayParts[2] : null,
          month: birthdayParts ? birthdayParts[1] : null,
          year: birthdayParts ? birthdayParts[0] : null
        }
      },
      issuedInStore: customerData.storeCode,
      customerEmails: {
        contactMethodTypeCode: "E",
        description: "Email",
        email: customerData.email || ""
      },
      customerPhones: {
        contactMethodTypeCode: "M",
        description: "Mobile",
        phone: customerData.mobilePhoneNumber || ""
      },
      histoDates: {
        creationTime: nowStr,
        modificationTime: nowStr
      },
      customerAddresses: {
        contactMethodTypeCode: "A",
        description: "Adresse",
        optinsContactMethod: {
          optin: "A",
          value: "true"
        },
        address: {
          addressLine3: get(address, "streetAddress2", null),
          postalCode: get(address, "postalCode", null),
          highwayLabel: get(address, "streetAddress1", null),
          city: get(address, "city", null),
          country: get(address, "country", null)
        }
      },
      datas_global: {
        code: "NSPC",
        data: {
          code:
            customerData.emailConsent ||
            customerData.postConsent ||
            customerData.phoneConsent
              ? "O"
              : "N"
        }
      },
      datas_postal: {
        code: "OPCO",
        data: {
          code: customerData.postConsent ? "O" : "N"
        }
      },
      datas_email: {
        code: "OPEM",
        data: {
          code: customerData.emailConsent ? "O" : "N"
        }
      },
      datas_phone: {
        code: "OPSM",
        data: {
          code: customerData.phoneConsent ? "O" : "N"
        }
      }
    }
  };
  return cylandObj;
};

export const toXML = function(obj: any) {
  var xml = "";
  for (var prop in obj) {
    xml += obj[prop] instanceof Array ? "" : "<" + prop + ">";
    if (obj[prop] instanceof Array) {
      for (var array in obj[prop]) {
        xml += "<" + prop + ">";
        xml += toXML(Object.create(obj[prop][array]));
        xml += "</" + prop + ">";
      }
    } else if (typeof obj[prop] == "object") {
      xml += toXML(Object.create(obj[prop]));
    } else {
      xml += obj[prop];
    }
    xml += obj[prop] instanceof Array ? "" : "</" + prop + ">";
  }
  xml = xml.replace(/<\/?[0-9]{1,}>/g, "");
  return xml;
};
